import { useEffect, useState } from "react";
import api, { errorMessage } from "../lib/api";
import { formatDateTime } from "../lib/assist";
import { ALERT_DELIVERIES, deliveryMissingGroup, lastGroupMessageText } from "../lib/whatsapp";
import { Button } from "./ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { Loader2, RefreshCw, UsersRound } from "lucide-react";

// Paramètres › Notifications: where one kind of team alert goes (`kind`: « assist » or « tickets », see lib/whatsapp.js
// ALERT_DELIVERIES). Controlled: the section's own dirty state / save bar owns the value. Listing the groups needs
// « Gérer le compte WhatsApp » (it reveals the account's conversations); the last group message is read from /whatsapp/status
// when the reader may see it.
export default function AlertDeliverySettings({ kind, value, onChange, canManage, canListGroups, canSeeStatus, disabled, children }) {
  const config = ALERT_DELIVERIES[kind];
  const [groups, setGroups] = useState(null); // null = not loaded
  const [loadingGroups, setLoadingGroups] = useState(false);
  const [groupsError, setGroupsError] = useState("");
  const [lastGroup, setLastGroup] = useState(null);
  const needsGroup = value.delivery === "group" || value.delivery === "both";
  const missing = deliveryMissingGroup(value);

  useEffect(() => {
    if (!canSeeStatus) return;
    api.get("/whatsapp/status").then(({ data }) => setLastGroup(data.queue?.last_group_message || null)).catch(() => setLastGroup(null));
  }, [canSeeStatus]);

  const loadGroups = async () => {
    setLoadingGroups(true);
    setGroupsError("");
    try {
      const { data } = await api.get("/whatsapp/groups");
      setGroups(data.groups);
    } catch (e) {
      setGroupsError(errorMessage(e));
    } finally {
      setLoadingGroups(false);
    }
  };

  const pickGroup = (jid) => {
    const group = (groups || []).find((g) => g.jid === jid);
    if (group) onChange({ ...value, group_jid: group.jid, group_name: group.name });
  };

  const last = lastGroupMessageText(lastGroup, formatDateTime);
  const editable = canManage && !disabled;

  return (
    <div className="p-5 border-t border-slate-100 space-y-3" data-testid={`${kind}-delivery`}>
      <div>
        <div className="text-sm font-medium text-slate-900">{config.title}</div>
        <p className="text-xs text-slate-500 mt-0.5">{config.hint}</p>
      </div>
      <div className="space-y-2" role="radiogroup" aria-label={config.title}>
        {config.options.map((option) => (
          <label key={option.value}
            className={`flex items-start gap-2.5 rounded-lg border px-3 py-2 ${editable ? "cursor-pointer" : "opacity-70"} ${value.delivery === option.value ? "border-jamora bg-jamora-50/50" : "border-slate-200"}`}>
            <input type="radio" name={`${kind}-delivery`} value={option.value} checked={value.delivery === option.value} disabled={!editable}
              onChange={() => onChange({ ...value, delivery: option.value })} className="mt-1 accent-jamora" data-testid={`${kind}-delivery-${option.value}`} />
            <span>
              <span className="block text-sm text-slate-900">{option.label}</span>
              <span className="block text-xs text-slate-500">{option.hint}</span>
            </span>
          </label>
        ))}
      </div>

      {needsGroup && (
        <div className="rounded-lg border border-slate-200 p-3 space-y-2" data-testid={`${kind}-group`}>
          <div className="flex items-center gap-2 text-sm">
            <UsersRound size={15} className="text-slate-400" />
            <span className="text-slate-600">Groupe :</span>
            <strong className="text-slate-900" data-testid={`${kind}-group-name`}>{value.group_name || (value.group_jid ? value.group_jid : "aucun")}</strong>
          </div>
          {editable && canListGroups && (
            groups === null ? (
              <Button type="button" variant="outline" size="sm" onClick={loadGroups} disabled={loadingGroups} data-testid={`${kind}-group-load`}>
                {loadingGroups ? <Loader2 size={14} className="animate-spin mr-1.5" /> : <RefreshCw size={14} className="mr-1.5" />}
                {value.group_jid ? "Changer de groupe" : "Choisir le groupe"}
              </Button>
            ) : groups.length ? (
              <Select value={value.group_jid || undefined} onValueChange={pickGroup}>
                <SelectTrigger data-testid={`${kind}-group-select`}><SelectValue placeholder="Choisir un groupe" /></SelectTrigger>
                <SelectContent>
                  {groups.map((g) => <SelectItem key={g.jid} value={g.jid}>{g.name}</SelectItem>)}
                </SelectContent>
              </Select>
            ) : (
              <p className="text-xs text-slate-500">Le compte WhatsApp du CRM ne fait partie d'aucun groupe. Ajoutez-le au groupe des associés, puis réessayez.</p>
            )
          )}
          {editable && !canListGroups && (
            <p className="text-xs text-slate-500">Choisir le groupe demande le droit « Gérer le compte WhatsApp ».</p>
          )}
          {groupsError && <p role="alert" className="text-xs text-red-700">{groupsError}</p>}
          {missing && <p role="alert" className="text-xs text-amber-700" data-testid={`${kind}-group-missing`}>{config.missing}</p>}
          <p className="text-xs text-slate-500">Le compte WhatsApp relié au CRM doit faire partie de ce groupe. {config.groupNote || "Tous les membres du groupe voient le détail de chaque demande."}</p>
        </div>
      )}

      {children}

      {last && (
        <p className={`text-xs ${last.tone === "error" ? "text-red-700" : last.tone === "ok" ? "text-slate-500" : "text-amber-700"}`} data-testid={`${kind}-group-last`}>
          {last.text}
        </p>
      )}
    </div>
  );
}
